import { ensureError } from '@passionware/platform-js';
import { createSimpleStore, useSimpleStore } from '@passionware/simple-store';
import { ErrorService } from './ErrorService';

export interface ErrorReporter {
  captureException(error: Error, hint: { tags: { category: string } }): void;
}

export function createErrorService(reporter: ErrorReporter): ErrorService {
  const interceptedErrors = createSimpleStore<
    { category: string; date: Date; error: Error }[]
  >([]);

  const service: ErrorService = {
    interceptErrors<Return>(category, fn) {
      try {
        const result = fn();
        if (result instanceof Promise) {
          return result.catch<Return>(error => {
            service.reportError(category, ensureError(error));
            throw error;
          });
        }
        return result;
      } catch (error) {
        service.reportError(category, ensureError(error));
        throw error;
      }
    },
    reportError(category, error) {
      reporter.captureException(ensureError(error), { tags: { category } });
      interceptedErrors.setNewValue(prev => [
        ...prev,
        { category, date: new Date(), error: ensureError(error) }
      ]);
    },
    useInterceptedErrors() {
      return useSimpleStore(interceptedErrors);
    },
    subscribeToErrors(category, callback) {
      interceptedErrors.addUpdateListener(prev => {
        const error = prev.find(e => e.category === category);
        if (error) {
          callback(error.error);
        }
      });
    },
    withCategory(category) {
      return {
        wrap(fn) {
          return (...args) => {
            return service.interceptErrors(category, () => fn(...args));
          };
        }
      };
    }
  };
  return service;
}
